import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Search, Pencil, Trash2, X, Sparkles } from "lucide-react";

const TYPES = ["facebook", "linkedin", "instagram", "blog", "email", "graphic", "video", "download"];
const STAGES = ["draft", "ready", "approved", "scheduled", "published", "archived"];
const STAGE_COLOR = {
  draft: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  ready: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  approved: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  scheduled: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  published: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  archived: "bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};

export default function ContentLibrary() {
  const [items, setItems] = useState(null);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (type) params.type = type;
    if (status) params.status = status;
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get("/ops/content", { params });
    setItems(data);
  }, [type, status, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this content item?")) return;
    await api.delete(`/ops/content/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Content Library</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Every post, article, email and asset in one register.</p>
        </div>
        <button onClick={() => setEditing({})} data-testid="ops-content-new"
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 rounded-lg font-semibold transition-colors">
          <Plus className="w-4 h-4" /> New content
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, notes, keywords…" data-testid="ops-content-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <Select value={type} onChange={setType} testid="ops-filter-type" all="All channels" options={TYPES} />
        <Select value={status} onChange={setStatus} testid="ops-filter-status" all="All stages" options={STAGES} />
      </div>

      {!items ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-12 text-center text-slate-400">
          No content yet. Click <strong>New content</strong> to add your first item.
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden">
          {items.map((it) => (
            <div key={it.id} data-testid={`ops-content-${it.id}`} className="flex items-center gap-4 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{it.title}</div>
                <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 dark:text-slate-400">
                  <span className="capitalize px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800">{it.type}</span>
                  {it.related_product && <span>· {it.related_product}</span>}
                  {it.publish_date && <span>· {it.publish_date}</span>}
                  {it.author && <span>· {it.author}</span>}
                </div>
              </div>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STAGE_COLOR[it.status]}`}>{it.status}</span>
              <button onClick={() => setEditing(it)} data-testid={`ops-content-edit-${it.id}`} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(it.id)} data-testid={`ops-content-del-${it.id}`} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-rose-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {editing && <ContentModal item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function Select({ value, onChange, options, all, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="px-3 py-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 capitalize focus:outline-none focus:ring-2 focus:ring-indigo-500">
      <option value="">{all}</option>
      {options.map((o) => <option key={o} value={o} className="capitalize">{o}</option>)}
    </select>
  );
}

function ContentModal({ item, onClose, onSaved }) {
  const [f, setF] = useState({
    title: item.title || "", type: item.type || "blog", category: item.category || "",
    status: item.status || "draft", keywords: (item.keywords || []).join(", "),
    related_product: item.related_product || "", publish_date: item.publish_date || "",
    author: item.author || "", notes: item.notes || "", body: item.body || "",
  });
  const [busy, setBusy] = useState(false);
  const [aiSource, setAiSource] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get("/forms").then((r) => setDocs(r.data)).catch(() => {}); }, []);

  const pickDoc = (id) => {
    const d = docs.find((x) => x.id === id);
    if (!d) return;
    setAiSource(`${d.title}${d.description ? "\n" + d.description : ""}`);
    if (!f.title.trim()) set("title", d.title);
  };

  const genAI = async () => {
    const src = aiSource.trim() || f.title.trim() || f.notes.trim();
    if (!src) return toast.error("Add a title or source topic for the AI to work from.");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ops/ai/generate", { source: src, content_type: f.type, tone: "professional and confident" }, { timeout: 90000 });
      const text = data.text || "";
      setF((p) => ({ ...p, body: text, title: p.title || text.replace(/^Subject:\s*/i, "").split("\n")[0].slice(0, 80) }));
      toast.success("AI draft generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed");
    } finally { setAiBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Title is required.");
    setBusy(true);
    const payload = { ...f, keywords: f.keywords.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (item.id) await api.put(`/ops/content/${item.id}`, payload);
      else await api.post("/ops/content", payload);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 w-full max-w-lg rounded-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-800">
          <h2 className="font-heading font-bold text-lg">{item.id ? "Edit content" : "New content"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="p-5 space-y-4">
          <Field label="Title"><input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="ops-content-title" className="inp" autoFocus /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Channel / type">
              <select value={f.type} onChange={(e) => set("type", e.target.value)} data-testid="ops-content-type" className="inp capitalize">
                {TYPES.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
              </select>
            </Field>
            <Field label="Stage">
              <select value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="ops-content-status" className="inp capitalize">
                {STAGES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category"><input value={f.category} onChange={(e) => set("category", e.target.value)} className="inp" /></Field>
            <Field label="Related product"><input value={f.related_product} onChange={(e) => set("related_product", e.target.value)} className="inp" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Publish date"><input type="date" value={f.publish_date} onChange={(e) => set("publish_date", e.target.value)} className="inp" /></Field>
            <Field label="Author"><input value={f.author} onChange={(e) => set("author", e.target.value)} className="inp" /></Field>
          </div>
          <Field label="Keywords (comma separated)"><input value={f.keywords} onChange={(e) => set("keywords", e.target.value)} className="inp" /></Field>

          <div className="rounded-lg border border-indigo-200 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950/30 p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700 dark:text-indigo-300">
              <Sparkles className="w-4 h-4" /> Generate with AI
            </div>
            <select onChange={(e) => pickDoc(e.target.value)} defaultValue="" data-testid="ops-ai-doc"
              className="inp">
              <option value="">From one of your documents… (optional)</option>
              {docs.map((d) => <option key={d.id} value={d.id}>{d.code ? d.code + " — " : ""}{d.title}</option>)}
            </select>
            <textarea value={aiSource} onChange={(e) => setAiSource(e.target.value)} rows={2} data-testid="ops-ai-source"
              placeholder="…or paste a document excerpt / describe the topic (blank uses the title above)" className="inp" />
            <button type="button" onClick={genAI} disabled={aiBusy} data-testid="ops-ai-generate"
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {aiBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Draft this {f.type}</>}
            </button>
          </div>

          <Field label="Body / draft"><textarea value={f.body} onChange={(e) => set("body", e.target.value)} rows={5} data-testid="ops-content-body" className="inp" /></Field>
          <Field label="Notes"><textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="inp" /></Field>
          <button type="submit" disabled={busy} data-testid="ops-content-save"
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-3 rounded-lg font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save content"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
